import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2, Loader2, Send, Mail } from 'lucide-react';
import { CONTACT, LOANS, INSURANCE } from '../data';

const API = '/enquiry.php';

const initialForm = { name: '', email: '', loan_type: 'Personal Loan', amount: '', message: '' };

export const EnquiryForm = () => {
    const [form, setForm] = useState(initialForm);
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    const isInsurance = INSURANCE.some((ins) => ins.value === form.loan_type);

    useEffect(() => {
        const handler = (e) =>
            setForm((f) => ({ ...f, loan_type: e.detail }));
        window.addEventListener('preselect-product', handler);
        return () => window.removeEventListener('preselect-product', handler);
    }, []);

    const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const data = new URLSearchParams();

            data.append('name', form.name);
            data.append('email', form.email);
            data.append(
                'message',
                `Loan/Insurance: ${form.loan_type}
Amount: ${form.amount || 'N/A'}
${form.message || ''}`
            );

            const response = await axios.post(API, data);

            if (!response.data.success) {
                throw new Error(response.data.message || 'Enquiry failed');
            }

            setDone(true);
            toast.success('Enquiry received! Our advisor will email you within 24 hours.');
            setForm(initialForm);
        } catch (err) {
            toast.error(
                err.response?.data?.message ||
                `Something went wrong. Please email us at ${CONTACT.email}`
            );
        } finally {
            setLoading(false);
        }
    };

    const inputCls =
        'w-full rounded-xl border border-white/10 bg-[#0D111A] px-4 py-3.5 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-emerald-400/60 focus:ring-1 focus:ring-emerald-400/40 transition-colors duration-300';

    return (
        <section id="apply" data-testid="enquiry-section" className="py-24 sm:py-32 relative overflow-hidden">
            <div className="absolute -bottom-40 -left-40 w-[480px] h-[480px] rounded-full bg-emerald-500/10 blur-[140px] pointer-events-none" />
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-14">
                <motion.div
                    initial={{ opacity: 0, y: 32 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-80px' }}
                    transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                >
                    <p className="font-mono-x text-xs uppercase tracking-[0.3em] text-emerald-400 mb-4">
                        03 — Begin
                    </p>
                    <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">
                        Two minutes now.{' '}
                        <span className="text-slate-500">A reply within 24 hours.</span>
                    </h2>
                    <p className="mt-5 text-sm sm:text-base leading-relaxed text-slate-400 max-w-md">
                        Fill the form and a senior advisor from Prem Associates will reach out on
                        email, shortlist the best bank for your profile, and handle everything
                        after — loans first, insurance if you need it.
                    </p>
                    <div className="mt-8 space-y-3 text-sm text-slate-300">
                        <div className="flex items-center gap-3">
                            <Mail className="w-4 h-4 text-emerald-400 shrink-0" />
                            <a href={`mailto:${CONTACT.email}`} data-testid="contact-email-link" className="hover:text-emerald-300 transition-colors">
                                {CONTACT.email}
                            </a>
                        </div>
                    </div>
                </motion.div>

                <motion.form
                    onSubmit={submit}
                    data-testid="enquiry-form"
                    initial={{ opacity: 0, y: 44 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-80px' }}
                    transition={{ duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
                    className="rounded-2xl border border-white/10 bg-[#131924] p-7 sm:p-9 space-y-5"
                >
                    {done && (
                        <div
                            data-testid="enquiry-success-banner"
                            className="flex items-center gap-2.5 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-300"
                        >
                            <CheckCircle2 className="w-4 h-4 shrink-0" />
                            Enquiry submitted. Expect our email within 24 hours.
                        </div>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <input
                            required
                            minLength={2}
                            value={form.name}
                            onChange={set('name')}
                            placeholder="Full name"
                            data-testid="enquiry-name-input"
                            className={inputCls}
                        />
                        <input
                            required
                            type="email"
                            value={form.email}
                            onChange={set('email')}
                            placeholder="Email address"
                            data-testid="enquiry-email-input"
                            className={inputCls}
                        />
                    </div>
                    <div>
                        <label
                            data-testid="enquiry-product-label"
                            className="block mb-2 font-mono-x text-[10px] uppercase tracking-[0.2em] text-slate-500"
                        >
                            {isInsurance ? 'Insurance Type' : 'Loan Type'}
                        </label>
                        <div className={`grid grid-cols-1 ${isInsurance ? '' : 'sm:grid-cols-2'} gap-5`}>
                            <select
                                value={form.loan_type}
                                onChange={set('loan_type')}
                                data-testid="enquiry-loan-type-select"
                                className={inputCls}
                            >
                                <optgroup label="Loans">
                                    {LOANS.map((l) => (
                                        <option key={l.id} value={l.value}>
                                            {l.title}
                                        </option>
                                    ))}
                                </optgroup>
                                <optgroup label="Insurance" data-testid="enquiry-insurance-optgroup">
                                    {INSURANCE.map((ins) => (
                                        <option key={ins.id} value={ins.value}>
                                            {ins.title}
                                        </option>
                                    ))}
                                </optgroup>
                            </select>
                            {!isInsurance && (
                                <input
                                    required
                                    type="number"
                                    min="10000"
                                    value={form.amount}
                                    onChange={set('amount')}
                                    placeholder="Loan amount (₹)"
                                    data-testid="enquiry-amount-input"
                                    className={inputCls}
                                />
                            )}
                        </div>
                    </div>
                    <textarea
                        rows={3}
                        value={form.message}
                        onChange={set('message')}
                        placeholder="Anything we should know? (optional)"
                        data-testid="enquiry-message-input"
                        className={`${inputCls} resize-none`}
                    />
                    <button
                        type="submit"
                        disabled={loading}
                        data-testid="enquiry-submit-button"
                        className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-500 py-4 text-sm font-semibold text-[#04110c] hover:bg-emerald-400 hover:shadow-[0_0_32px_rgba(16,185,129,0.4)] disabled:opacity-60 transition-all duration-300"
                    >
                        {loading ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                            <Send className="w-4 h-4" />
                        )}
                        {loading ? 'Submitting…' : 'Send Enquiry'}
                    </button>
                    <p className="text-[11px] text-slate-600 text-center">
                        Zero spam. Your details stay between you, us and the bank.
                    </p>
                </motion.form>
            </div>
        </section>
    );
};
